import { BoardMembersFormViewModel } from 'src/types/BoardMembersFormViewModel';
import { BoardMembersListItemModel } from 'src/types/BoardMembersListItemModel';
import { ApiResponseModel } from '../types/ApiResponseModel';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';
import { BoardMembersItemModel } from 'src/types/BoardMembersItemModel';
import { SelectOption } from 'src/types/SelectOption'; // Ensure this path is correct

const BoardMembersIndex = 1;
export const boardMembersApiPaths = {

  getListItems: () =>
    `boardmember`,
  getItemById: (boardMemberId: string, leaId: string | undefined) =>
    `boardmember/${leaId}/${boardMemberId}`,
  deleteItem: (boardMemberId: string, leaId: string | undefined) =>
    `boardmember/${leaId}/${boardMemberId}`,
  getSchoolYears: (moduleId: number, showLongYears = false) => 
    `common/schoolyearsbymodule/formview/${moduleId}?showLongYears=${showLongYears}`,
  create: 'boardMember',
  update: 'boardMember',
  getForm: 'boardmember/formview',
  getStateOptions: 'common/statelist'
};

export const BoardMembersServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const getBoardMembersListItems = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<BoardMembersListItemModel[]>> => {
  return executeApiRequest<BoardMembersListItemModel[]>(async () => {
    return await BoardMembersServiceClient(config).get(
      boardMembersApiPaths.getListItems()
    );
  });
};

export const getBoardMembersItemById = async (
  boardMemberId: string,
  leaId: string | undefined,
  config?: DataRequestConfig
): Promise<ApiResponseModel<BoardMembersItemModel>> => {
  return executeApiRequest<BoardMembersItemModel>(async () => {
    const response = await BoardMembersServiceClient(config).get<BoardMembersItemModel[]>(
      boardMembersApiPaths.getItemById(boardMemberId, leaId)
    );
    // Check if the response is successful and contains data
    if (response.data && response.data.length > 0) {
      // Return the first item from the array
      return { ...response, data: response.data[0] };
    } else {
      // Handle the case where the array is empty or undefined
      return { ...response, data: {} as BoardMembersItemModel };
    }
  });
};

export const createBoardMember = async (
  body: BoardMembersItemModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {

    // Create the payload *without* the id from the form body
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...restOfBody } = body; // Destructure to separate id

    const payload = { // Type assertion might be needed if TS complains
      ...restOfBody, // Use only the rest of the fields
      // Explicitly DO NOT include 'id' here for creation
    };

    return await BoardMembersServiceClient(config).post(
      boardMembersApiPaths.create,
      payload
    );
  });
};

export const deleteBoardMember = async (
  boardMemberId: string,
  leaId: string | undefined,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => { 
  return executeApiRequest<unknown>(async () => { // Match the return type
    const response = await BoardMembersServiceClient(config).delete(
      boardMembersApiPaths.deleteItem(boardMemberId, leaId)
    );
    // executeApiRequest handles status checking, just return the response
    return response;
  });
};

export const updateBoardMembers = async (
  body: BoardMembersItemModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<unknown>> => {
  return executeApiRequest<unknown>(async () => {
    return await BoardMembersServiceClient(config).put(
      boardMembersApiPaths.update,
      body
    );
  });
};

export const getBoardMembersFormViewModel = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<BoardMembersFormViewModel>> => {
  return executeApiRequest<BoardMembersFormViewModel>(async () => {
    return await BoardMembersServiceClient(config).get(
      boardMembersApiPaths.getForm
    );
  });
};

export const getStateOptions = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await BoardMembersServiceClient(config).get(
      boardMembersApiPaths.getStateOptions
    );
  });
};

export const getBoardMembersSchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await BoardMembersServiceClient(config).get(
      boardMembersApiPaths.getSchoolYears(BoardMembersIndex, true)
    );
  });
};